/**
 * EditApplier — the ONE place a span-anchored TextEdit becomes a buffer write
 * (WYSIWYG plan §5). Two routes, one funnel:
 *
 *   - a live Monaco model exists for the file (Editor registers every model it
 *     creates): apply via `pushEditOperations` bracketed by `pushStackElement`
 *     so ONE gesture = ONE undo stop, and the model's own change subscription
 *     forwards the new content to the store like typing — editor ⌘Z undoes a
 *     board gesture, cursor/view state survive;
 *   - no model: `store.edit(path, appliedText)` — the plain store route.
 *
 * The registry is keyed by store instance (WeakMap), so multiple workbench
 * compositions on one page can't cross wires, and paths are keyed exactly as
 * the STORE keys them (bare or "/"-prefixed) — callers translate from the
 * compiler's absolute VFS keys first (see mutations.ts `storeKeyFor`).
 */

import type { TextEdit } from "@archlang/engine";
import { applyTextEdits } from "@archlang/engine";

/** The minimal Monaco ITextModel surface the applier needs (kept structural so
 *  tests can drive a fake and this module never imports monaco). */
export interface ApplierModel {
  getValue(): string;
  getPositionAt(offset: number): { lineNumber: number; column: number };
  pushStackElement(): void;
  pushEditOperations(
    beforeCursorState: null,
    editOperations: {
      range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number };
      text: string;
    }[],
    cursorStateComputer: () => null,
  ): unknown;
}

interface StoreLike {
  getState(): { files: ReadonlyMap<string, string> };
  edit(path: string, content: string, edits?: readonly TextEdit[]): void;
}

const registries = new WeakMap<object, Map<string, ApplierModel>>();

function registryFor(store: object): Map<string, ApplierModel> {
  let r = registries.get(store);
  if (!r) {
    r = new Map();
    registries.set(store, r);
  }
  return r;
}

/** Editor primitives register each Monaco model they create (per store, per
 *  store-keyed path) and unregister on disposal. */
export function registerEditorModel(store: object, path: string, model: ApplierModel): void {
  registryFor(store).set(path, model);
}

export function unregisterEditorModel(store: object, path: string, model?: ApplierModel): void {
  const r = registries.get(store);
  if (!r) return;
  if (model && r.get(path) !== model) return; // a newer model already took the slot
  r.delete(path);
}

/** True when a live Monaco model backs this file (undo-integrated route). */
export function hasEditorModel(store: object, path: string): boolean {
  return registries.get(store)?.has(path) ?? false;
}

export type ApplyRoute = "monaco" | "store";

/**
 * Apply `edits` (UTF-16 offsets against the file's CURRENT text) to `path`.
 * Returns which route carried the write. Throws if the store has no such file.
 */
export function applyEditsToFile(store: StoreLike, path: string, edits: readonly TextEdit[]): ApplyRoute {
  const model = registries.get(store)?.get(path);
  const storeText = store.getState().files.get(path);
  // The monaco route is only sound while the model MIRRORS the store: a
  // background (non-active) model missed external store writes (revert, disk
  // sync — Editor only syncs the active file), so pushing offset edits into
  // it would splice at wrong positions AND its change-sub would then clobber
  // the store with the corrupt buffer. Desynced model → store route; the
  // model itself resyncs on activation.
  if (model && storeText !== undefined && model.getValue() === storeText) {
    const ops = edits.map((e) => {
      const start = model.getPositionAt(e.start);
      const end = model.getPositionAt(e.end);
      return {
        range: {
          startLineNumber: start.lineNumber,
          startColumn: start.column,
          endLineNumber: end.lineNumber,
          endColumn: end.column,
        },
        text: e.newText,
      };
    });
    model.pushStackElement();
    model.pushEditOperations(null, ops, () => null);
    model.pushStackElement();
    return "monaco";
  }

  const text = store.getState().files.get(path);
  if (text === undefined) throw new Error(`applyEditsToFile: no file '${path}' in store`);
  // Carry the batch alongside the resulting content: a source that can splice
  // (an IDE host owning the live buffer) writes the edits; one that can't falls
  // back to the content. The monaco route above needs no such hand-off — the
  // model's own change subscription already forwards the spliced text.
  store.edit(path, applyTextEdits(text, edits), edits);
  return "store";
}
